// Interactive SVG-template editing helpers for the New Design authoring canvas.
//
// These operate on LIVE DOM elements (getBBox / getCTM need layout) inside an
// injected <svg>. The flow: a rep clicks artwork -> we select the top-level
// component -> "Make Team Name" / "Logo Zone" REPLACES that selection with a
// tagged <text> / <rect> placed at the selection's bounding box in ROOT
// (viewBox) coordinates, so any ancestor transforms are accounted for. The
// renderer (sport-templates svg-renderer) then substitutes school data into the
// data-slot / data-color tags at render time.

export type SlotKind = string; // data-slot id, e.g. 'team_text', 'school_initials', 'year_yy'

// Authoring slot types offered in the editor. value = data-slot, role = default colorway index.
export const SLOT_TYPES: { value: string; label: string; placeholder: string; role: string }[] = [
  { value: 'team_text', label: 'Team Name', placeholder: 'TEAM NAME', role: 'primary' },
  { value: 'mascot_text', label: 'Mascot', placeholder: 'MASCOT', role: 'secondary' },
  { value: 'school_text', label: 'School Name', placeholder: 'SCHOOL NAME', role: 'primary' },
  { value: 'sport', label: 'Sport', placeholder: 'SPORT', role: 'primary' },
  { value: 'year', label: 'Year (full)', placeholder: 'EST. 2026', role: 'primary' },
  { value: 'year_y1y2', label: 'Year — first half', placeholder: '20', role: 'secondary' },
  { value: 'year_yy', label: 'Year — second half', placeholder: '26', role: 'secondary' },
  { value: 'school_initials', label: 'School Initials', placeholder: 'CD', role: 'primary' },
  { value: 'number', label: 'Number', placeholder: '00', role: 'primary' },
  { value: 'player_name', label: 'Player Name', placeholder: 'PLAYER', role: 'primary' },
  { value: 'last_name', label: 'Last Name', placeholder: 'LAST', role: 'primary' },
  { value: 'coach_name', label: 'Coach Name', placeholder: 'COACH', role: 'primary' },
  { value: 'league_text', label: 'League', placeholder: 'LEAGUE', role: 'secondary' },
  { value: 'tournament_text', label: 'Tournament', placeholder: 'TOURNAMENT', role: 'secondary' },
  { value: 'city_text', label: 'City', placeholder: 'CITY', role: 'secondary' },
  { value: 'tagline', label: 'Tagline', placeholder: 'GO BIG', role: 'primary' },
];

export const SLOT_LABELS: Record<string, string> = Object.fromEntries(SLOT_TYPES.map((s) => [s.value, s.placeholder]));

// Fonts installed in the sport-templates render image (keep in sync with sport-templates/fonts/).
export const INSTALLED_FONTS = ['Anton', 'Bebas Neue', 'Archivo Black', 'Alfa Slab One', 'Graduate', 'Russo One', 'League Gothic', 'Changa One', 'Teko', 'Karantina', 'Faster One', 'Pacifico'];

// Text effects the renderer supports.
export const EFFECTS = [
  { value: 'straight', label: 'Straight' },
  { value: 'arch_up', label: 'Arch ⌒ up' },
  { value: 'arch_down', label: 'Arch ⌣ down' },
  { value: 'slant_up', label: 'Slant ↗' },
  { value: 'slant_down', label: 'Slant ↘' },
];

const DEFAULT_FONT = 'Anton';

const SVGNS = 'http://www.w3.org/2000/svg';
const ROLE_FILL: Record<string, string> = { primary: '#1E4EB5', secondary: '#E8B61A', tertiary: '#2A2A2A', accent: '#444444' };

// Climb to the ancestor that is a direct child of the <svg> root (the "component"
// under the cursor). Falls back to the element itself.
export function topComponent(el: Element, root: SVGSVGElement): Element {
  let cur: Element | null = el;
  while (cur && cur.parentNode && cur.parentNode !== root) cur = cur.parentNode as Element;
  return cur && cur.parentNode === root ? cur : el;
}

// Bounding box of `el` expressed in the ROOT viewBox coordinate system (accounts
// for every ancestor transform), so replacements can be inserted at root level.
export function rootBBox(el: SVGGraphicsElement, root: SVGSVGElement) {
  const bb = el.getBBox();
  // Map the element's local coordinates into the ROOT's user (viewBox) coordinate
  // system. getCTM() folds in the viewBox->viewport scale (giving display pixels
  // that vary with the canvas size); composing screen CTMs cancels the shared
  // viewport scale and yields true, size-independent viewBox units.
  const elScreen = el.getScreenCTM();
  const rootScreen = root.getScreenCTM();
  const m = elScreen && rootScreen ? rootScreen.inverse().multiply(elScreen) : (el.getCTM() || root.createSVGMatrix());
  const corners = [
    [bb.x, bb.y], [bb.x + bb.width, bb.y],
    [bb.x, bb.y + bb.height], [bb.x + bb.width, bb.y + bb.height],
  ].map(([x, y]) => ({ x: m.a * x + m.c * y + m.e, y: m.b * x + m.d * y + m.f }));
  const xs = corners.map((p) => p.x), ys = corners.map((p) => p.y);
  const minX = Math.min(...xs), minY = Math.min(...ys);
  return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
}

// Tag `el` as a text slot. If it is already <text>/<tspan>, tag in place;
// otherwise REPLACE it with a <text> placeholder at its root bounding box.
export function makeTextSlot(el: SVGGraphicsElement, root: SVGSVGElement, slot: SlotKind, role: string, font: string = DEFAULT_FONT): SVGElement {
  const placeholder = SLOT_LABELS[slot] || slot.replace(/_/g, ' ').toUpperCase();
  const tag = el.tagName.toLowerCase();
  if (tag === 'text' || tag === 'tspan') {
    el.setAttribute('data-slot', slot);
    el.setAttribute('data-color', role);
    if (font) el.setAttribute('font-family', font);
    return el;
  }
  const box = rootBBox(el, root);
  const fontSize = Math.max(6, box.height / 0.72);
  const t = document.createElementNS(SVGNS, 'text');
  t.setAttribute('data-slot', slot);
  t.setAttribute('data-color', role);
  t.setAttribute('data-fit', 'scale');
  t.setAttribute('data-maxwidth', String(Math.round(box.width)));
  t.setAttribute('x', String(Math.round(box.x + box.width / 2)));
  t.setAttribute('y', String(Math.round(box.y + box.height)));
  t.setAttribute('text-anchor', 'middle');
  t.setAttribute('font-family', font);
  t.setAttribute('font-size', String(Math.round(fontSize)));
  t.setAttribute('fill', ROLE_FILL[role] || '#222222');
  t.textContent = placeholder;
  root.appendChild(t);
  el.remove();
  return t;
}

// Set the font-family on a selected text slot.
export function setFont(el: Element, family: string) {
  if (family) el.setAttribute('font-family', family);
}

// Set (or clear) a text effect on a selected text slot. ratio controls arch curvature / slant angle.
export function setEffect(el: Element, effect: string, ratio = 1.5) {
  if (!effect || effect === 'straight') {
    el.removeAttribute('data-effect');
    el.removeAttribute('data-ratio');
  } else {
    el.setAttribute('data-effect', effect);
    el.setAttribute('data-ratio', String(ratio));
  }
}

// Replace `el` with a logo-zone rect at its root bounding box.
export function makeLogoZone(el: SVGGraphicsElement, root: SVGSVGElement): SVGElement {
  const box = rootBBox(el, root);
  const r = document.createElementNS(SVGNS, 'rect');
  r.setAttribute('data-slot', 'logo_zone');
  r.setAttribute('x', String(Math.round(box.x)));
  r.setAttribute('y', String(Math.round(box.y)));
  r.setAttribute('width', String(Math.round(box.width)));
  r.setAttribute('height', String(Math.round(box.height)));
  r.setAttribute('fill', '#EAEAEA');
  root.appendChild(r);
  el.remove();
  return r;
}

export function setColorRole(el: Element, role: string) {
  el.setAttribute('data-color', role);
  // Also reflect the role visually so the canvas updates immediately.
  if (ROLE_FILL[role]) {
    const tag = el.tagName.toLowerCase();
    if (tag === 'text' || tag === 'tspan' || tag === 'path' || tag === 'rect' || tag === 'circle' || tag === 'ellipse' || tag === 'polygon') {
      el.setAttribute('fill', ROLE_FILL[role]);
    }
  }
}

// ─── Color detection + bulk slotting (fast onboarding of uploaded artwork) ────

// Normalize a CSS color (hex / rgb()/rgba() / rgb(%)) to #rrggbb, or null for
// none/transparent/url() paints.
export function cssColorToHex(v: string | null): string | null {
  if (!v) return null;
  const s = v.trim().toLowerCase();
  if (!s || s === 'none' || s === 'transparent' || s.startsWith('url(')) return null;
  if (s.startsWith('#')) {
    if (s.length === 4) return '#' + s.slice(1).split('').map((c) => c + c).join('');
    return s.length >= 7 ? s.slice(0, 7) : null;
  }
  const m = s.match(/rgba?\(([^)]+)\)/);
  if (!m) return null;
  const nums = m[1].split(',').slice(0, 3).map((p) => p.trim()).map((p) => (p.endsWith('%') ? Math.round(parseFloat(p) * 2.55) : parseInt(p, 10)));
  if (nums.length < 3 || nums.some((n) => Number.isNaN(n))) return null;
  return '#' + nums.map((n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')).join('');
}

const SHAPE_SEL = 'path,rect,circle,ellipse,polygon,polyline,line';

// Distinct source colors used as fills (computed style resolves inline styles +
// CSS classes), most-used first. Skips already-tagged shapes + the logo zone.
export function detectColorsInRoot(root: SVGSVGElement): { color: string; count: number }[] {
  const counts: Record<string, number> = {};
  root.querySelectorAll(SHAPE_SEL).forEach((el) => {
    if ((el as Element).id === '__selbox' || el.hasAttribute('data-color') || el.getAttribute('data-slot') === 'logo_zone') return;
    const hex = cssColorToHex(getComputedStyle(el as Element).getPropertyValue('fill'));
    if (hex) counts[hex] = (counts[hex] || 0) + 1;
  });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([color, count]) => ({ color, count }));
}

// Tag every shape whose resolved fill equals sourceHex with data-color=role and
// reflect the role color on the canvas. Returns the count affected.
export function bulkTagColor(root: SVGSVGElement, sourceHex: string, role: string): number {
  let n = 0;
  const swatch = ROLE_FILL[role] || ROLE_FILL.primary;
  root.querySelectorAll(SHAPE_SEL).forEach((el) => {
    if ((el as Element).id === '__selbox') return;
    const hex = cssColorToHex(getComputedStyle(el as Element).getPropertyValue('fill'));
    if (!hex || hex.toLowerCase() !== sourceHex.toLowerCase()) return;
    el.setAttribute('data-color', role);
    el.setAttribute('fill', swatch);
    const style = el.getAttribute('style');
    if (style && /fill\s*:/.test(style)) el.setAttribute('style', style.replace(/fill\s*:\s*[^;]+;?/i, `fill:${swatch};`));
    n++;
  });
  return n;
}

// ─── Stroke stack + arch apex (read/write data-strokes / data-arch-center) ────

export type StrokeSpec = { color: string; width: number };

export function getStrokes(el: Element): StrokeSpec[] {
  const raw = el.getAttribute('data-strokes');
  if (!raw) return [];
  try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
}
export function setStrokes(el: Element, strokes: StrokeSpec[]) {
  if (!strokes || !strokes.length) el.removeAttribute('data-strokes');
  else el.setAttribute('data-strokes', JSON.stringify(strokes));
}
export function getArchCenter(el: Element): number {
  const v = parseFloat(el.getAttribute('data-arch-center') || '');
  return Number.isFinite(v) ? v : 0.5;
}
export function setArchCenter(el: Element, center: number) {
  if (center == null || Number.isNaN(center) || Math.abs(center - 0.5) < 1e-6) el.removeAttribute('data-arch-center');
  else el.setAttribute('data-arch-center', String(center));
}

// Clean serialization for preview/save: drop editor-only artifacts + display sizing.
export function serializeClean(root: SVGSVGElement): string {
  const clone = root.cloneNode(true) as SVGSVGElement;
  clone.querySelectorAll('#__selbox').forEach((n) => n.remove());
  clone.removeAttribute('style');
  clone.removeAttribute('width');
  clone.removeAttribute('height');
  if (!clone.getAttribute('xmlns')) clone.setAttribute('xmlns', SVGNS);
  return clone.outerHTML;
}

export function summarizeSlots(root: SVGSVGElement): { textSlots: string[]; hasLogo: boolean; colorRoles: string[] } {
  const textSlots: string[] = [];
  let hasLogo = false;
  root.querySelectorAll('[data-slot]').forEach((n) => {
    const s = n.getAttribute('data-slot') || '';
    if (s === 'logo_zone') hasLogo = true;
    else if (s) textSlots.push(s);
  });
  const colorRoles = Array.from(new Set(Array.from(root.querySelectorAll('[data-color]')).map((n) => n.getAttribute('data-color') || '').filter(Boolean)));
  return { textSlots: Array.from(new Set(textSlots)), hasLogo, colorRoles };
}

// ─── Zone create / move / resize / props + listZones ─────────────────────────

export type Box = { x: number; y: number; w: number; h: number };
export type ZoneKind = 'text' | 'logo';
export type Zone = { el: SVGElement; kind: ZoneKind; role: string; box: Box };

// Inclusive point-in-box hit test (point and box share one coordinate system,
// e.g. root viewBox units). Used to make the ENTIRE authored zone box draggable
// — a pointer anywhere inside the selected zone's box starts a move, not just
// its text glyph or one of the resize handles.
export function pointInBox(p: { x: number; y: number }, box: Box): boolean {
  return p.x >= box.x && p.x <= box.x + box.w && p.y >= box.y && p.y <= box.y + box.h;
}

export function createTextZone(root: SVGSVGElement, box: Box, role: string, font = DEFAULT_FONT): SVGElement {
  const def = SLOT_TYPES.find((s) => s.value === role);
  const t = document.createElementNS(SVGNS, 'text');
  t.setAttribute('data-slot', role);
  t.setAttribute('data-zone', `${Math.round(box.x)},${Math.round(box.y)},${Math.round(box.w)},${Math.round(box.h)}`);
  t.setAttribute('data-anchor', 'cm');
  t.setAttribute('data-color', def ? def.role : 'primary');
  t.setAttribute('font-family', font);
  t.setAttribute('font-size', String(Math.max(6, Math.round(box.h * 0.7))));
  t.setAttribute('text-anchor', 'middle');
  t.setAttribute('x', String(Math.round(box.x + box.w / 2)));
  t.setAttribute('y', String(Math.round(box.y + box.h * 0.75)));
  t.setAttribute('fill', ROLE_FILL[def ? def.role : 'primary'] || '#222');
  t.textContent = (SLOT_LABELS[role] || role.replace(/_/g, ' ').toUpperCase());
  root.appendChild(t);
  return t;
}

export function createLogoZone(root: SVGSVGElement, box: Box): SVGElement {
  const rc = document.createElementNS(SVGNS, 'rect');
  rc.setAttribute('data-slot', 'logo_zone');
  rc.setAttribute('data-anchor', 'cm'); rc.setAttribute('data-pad', '0');
  rc.setAttribute('x', String(Math.round(box.x))); rc.setAttribute('y', String(Math.round(box.y)));
  rc.setAttribute('width', String(Math.round(box.w))); rc.setAttribute('height', String(Math.round(box.h)));
  rc.setAttribute('fill', '#EAEAEA');
  root.appendChild(rc);
  return rc;
}

export function zoneBox(el: SVGElement): Box {
  if (el.tagName.toLowerCase() === 'text') {
    const p = (el.getAttribute('data-zone') || '0,0,0,0').split(',').map(Number);
    return { x: p[0], y: p[1], w: p[2], h: p[3] };
  }
  return { x: +(el.getAttribute('x') || 0), y: +(el.getAttribute('y') || 0), w: +(el.getAttribute('width') || 0), h: +(el.getAttribute('height') || 0) };
}

export function setZoneBox(el: SVGElement, box: Box) {
  const b = { x: Math.round(box.x), y: Math.round(box.y), w: Math.round(box.w), h: Math.round(box.h) };
  if (el.tagName.toLowerCase() === 'text') {
    el.setAttribute('data-zone', `${b.x},${b.y},${b.w},${b.h}`);
    el.setAttribute('font-size', String(Math.max(6, Math.round(b.h * 0.7))));
    const a = el.getAttribute('text-anchor') || 'middle';
    el.setAttribute('x', String(a === 'start' ? b.x : a === 'end' ? b.x + b.w : b.x + b.w / 2));
    el.setAttribute('y', String(b.y + b.h * 0.75));
  } else {
    el.setAttribute('x', String(b.x)); el.setAttribute('y', String(b.y));
    el.setAttribute('width', String(b.w)); el.setAttribute('height', String(b.h));
  }
}

export function setAnchor(el: SVGElement, anchor: string) { el.setAttribute('data-anchor', anchor); setZoneBox(el, zoneBox(el)); }
export function setPad(el: SVGElement, pad: number) { el.setAttribute('data-pad', String(Math.max(0, pad))); }
export function setTracking(el: SVGElement, t: number) { if (t) el.setAttribute('data-tracking', String(t)); else el.removeAttribute('data-tracking'); }

export function listZones(root: SVGSVGElement): Zone[] {
  return Array.from(root.querySelectorAll('[data-slot]')).map((n) => {
    const el = n as SVGElement; const slot = el.getAttribute('data-slot') || '';
    return { el, kind: slot === 'logo_zone' ? 'logo' : 'text', role: slot, box: zoneBox(el) } as Zone;
  });
}

export function snapZoneToElement(el: SVGGraphicsElement, root: SVGSVGElement, role: string, font = DEFAULT_FONT): SVGElement {
  const r = rootBBox(el, root); el.remove();
  return createTextZone(root, { x: r.x, y: r.y, w: r.width, h: r.height }, role, font);
}

// ─── Align / distribute layout math ──────────────────────────────────────────

export type Align = 'left' | 'center-h' | 'right' | 'top' | 'center-v' | 'bottom';

export function alignBoxes(boxes: Box[], how: Align, canvasSize = 1000): Box[] {
  if (!boxes.length) return boxes;
  const minX = Math.min(...boxes.map((b) => b.x)), maxX = Math.max(...boxes.map((b) => b.x + b.w));
  const minY = Math.min(...boxes.map((b) => b.y)), maxY = Math.max(...boxes.map((b) => b.y + b.h));
  const single = boxes.length === 1;
  return boxes.map((b) => {
    const nb = { ...b };
    if (how === 'left') nb.x = single ? 0 : minX;
    else if (how === 'right') nb.x = (single ? canvasSize : maxX) - b.w;
    else if (how === 'center-h') nb.x = canvasSize / 2 - b.w / 2;
    else if (how === 'top') nb.y = single ? 0 : minY;
    else if (how === 'bottom') nb.y = (single ? canvasSize : maxY) - b.h;
    else if (how === 'center-v') nb.y = canvasSize / 2 - b.h / 2;
    return nb;
  });
}

export function distributeBoxes(boxes: Box[], axis: 'h' | 'v', mode: 'gaps' | 'centers'): Box[] {
  if (boxes.length < 3) return boxes;
  const k = axis === 'h' ? 'x' : 'y', d = axis === 'h' ? 'w' : 'h';
  const idx = boxes.map((_b, i) => i).sort((a, b) => (boxes[a] as any)[k] - (boxes[b] as any)[k]);
  const first = boxes[idx[0]], last = boxes[idx[idx.length - 1]];
  const out = boxes.map((b) => ({ ...b }));
  if (mode === 'centers') {
    const c0 = (first as any)[k] + (first as any)[d] / 2, c1 = (last as any)[k] + (last as any)[d] / 2;
    idx.forEach((bi, i) => { const c = c0 + (c1 - c0) * (i / (idx.length - 1)); (out[bi] as any)[k] = c - (out[bi] as any)[d] / 2; });
  } else {
    const totalW = idx.reduce((s, bi) => s + (boxes[bi] as any)[d], 0);
    const span = ((last as any)[k] + (last as any)[d]) - (first as any)[k];
    const gap = (span - totalW) / (idx.length - 1);
    let cursor = (first as any)[k];
    idx.forEach((bi) => { (out[bi] as any)[k] = cursor; cursor += (boxes[bi] as any)[d] + gap; });
  }
  return out;
}
